import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const field = "h-12 w-full rounded-xl border border-black/10 bg-white px-4 text-[15px] text-ink outline-none placeholder:text-mist/70 focus:border-brand focus:ring-4 focus:ring-brand/15";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success("Welcome to SpeakToPrompt");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="stp-bg flex min-h-screen flex-col items-center justify-center px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
        <div className="mb-10 flex justify-center"><Brand /></div>
        <h1 className="text-center font-display text-4xl font-semibold text-ink">{mode === "login" ? "Welcome back." : "Start free."}</h1>
        <p className="mt-2 text-center text-base text-mist">{mode === "login" ? "Sign in to your prompt workspace." : localStorage.getItem("stp_ref") ? "A friend invited you: 14 days free. No credit card." : "7 days free. No credit card."}</p>

        <button data-testid="google-signin-button" onClick={google} className="mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-full border border-black/10 bg-white text-[15px] font-medium text-ink transition-colors hover:bg-black/[0.03]">
          <GoogleLogo size={18} weight="bold" /> Continue with Google
        </button>
        <div className="my-6 flex items-center gap-3 text-xs text-mist"><span className="h-px flex-1 bg-black/10" /> or with email <span className="h-px flex-1 bg-black/10" /></div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && <input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={field} />}
          <input data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
          <input data-testid="password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (6+ characters)" className={field} />
          {mode === "login" && (
            <div className="flex justify-end">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="py-1 text-sm font-medium text-brand hover:underline">Forgot password?</Link>
            </div>
          )}
          <button data-testid="auth-submit-button" type="submit" disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand text-[15px] font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-60">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"} {!busy && <ArrowRight size={16} weight="bold" />}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-mist">
          {mode === "login" ? "New to SpeakToPrompt?" : "Already have an account?"}{" "}
          <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "signup" : "login")} className="font-medium text-brand hover:underline">
            {mode === "login" ? "Start free trial" : "Sign in"}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
